import type { ReactElement } from "react";
import { useEffect, useRef, useState } from "react";

import { getCanHover } from "@ui/utils/getCanHover";

import { Tooltip } from "../Tooltip";

type TruncatedTooltipProps = {
  content: string;
  iconClassName?: string;
  wrapperClassName?: string;
  children: ReactElement;
};

export const TruncatedTooltip = ({ content, children, iconClassName, wrapperClassName }: TruncatedTooltipProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const canHover = getCanHover();

  const checkTruncation = useRef(() => {
    const el = containerRef.current?.firstElementChild;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
    }
  });

  useEffect(() => {
    checkTruncation.current();
  }, [content]);

  useEffect(() => {
    const el = containerRef.current?.firstElementChild;
    if (!el) return;

    const observer = new ResizeObserver(() => checkTruncation.current());
    observer.observe(el);

    return () => observer.disconnect();
  });

  const latestChildren = useRef(children);
  latestChildren.current = children;

  const Slot = () => (
    <div ref={containerRef} style={{ display: "contents" }}>
      {latestChildren.current}
    </div>
  );

  if (isTruncated) {
    return (
      <Tooltip
        content={content}
        behaviour={canHover ? "hover" : "click"}
        IconSlot={Slot}
        iconClassName={iconClassName}
        wrapperClassName={wrapperClassName}
      />
    );
  }

  return (
    <div ref={containerRef} className={wrapperClassName}>
      {children}
    </div>
  );
};
